import { useState, useRef, useEffect } from 'react';
import BasePage from './BasePage';
import { 
  Snackbar, 
  Alert, 
  Box, 
  Typography, 
  CircularProgress,
  Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TuneIcon from '@mui/icons-material/Tune';
import { useNavigate } from 'react-router-dom';
import { useHomes } from '../hooks/useHomes';
import { useAuth } from '../contexts/AuthContext';
import AddHomeForm from '../components/AddHomeForm';
import SearchField from '../components/SearchField';
import HomePreferencesDialog from '../components/HomePreferencesDialog';
import HomesGrid from '../components/HomesGrid';

function Homes() {
  const navigate = useNavigate();
  const { user } = useAuth(); // Get user from auth context
  const { 
    homes, 
    isLoading, 
    fetchHomes, 
    createHome, 
    deleteHome, 
    updateHome,
    isSubmitting,
    deletingHomeIds 
  } = useHomes();
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const textFieldRef = useRef<HTMLInputElement>(null);
  const [editHomeId, setEditHomeId] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [homeToEdit, setHomeToEdit] = useState<{ 
    name: string, 
    address: string,
    bedrooms?: number, 
    bathrooms?: number, 
    sqft?: number,
    price?: number
  }>({ name: '', address: '' });
  
  // Add state for preferences dialog
  const [preferencesDialogOpen, setPreferencesDialogOpen] = useState(false);
  const [homePreferences, setHomePreferences] = useState('');
  const [preferencesMessage, setPreferencesMessage] = useState({ 
    type: 'success' as 'success' | 'error', 
    text: '' 
  });

  // Load preferences from user object when it changes
  useEffect(() => {
    if (user) {
      setHomePreferences(user.home_preferences || '');
    }
  }, [user]);

  // Filter homes based on search query
  const filteredHomes = homes.filter(home => {
    // Check if name matches
    const nameMatch = home.name.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Try to extract address from details
    let address = '';
    try {
      if (typeof home.details === 'string') {
        if (home.details.startsWith('{')) {
          // If details is a JSON string
          const detailsObj = JSON.parse(home.details);
          address = detailsObj.address || '';
        } else {
          // If details is directly the address
          address = home.details;
        }
      }
    } catch (e) {
      // If parsing fails, use details as is
      address = home.details || '';
    }
    
    // Check if address matches
    const addressMatch = address.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Return true if either name or address matches
    return nameMatch || addressMatch;
  });

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handlePreferencesOpen = () => {
    setPreferencesDialogOpen(true);
  };

  const handlePreferencesClose = () => {
    setPreferencesDialogOpen(false);
  };

  const handleSavePreferences = async (preferences: string) => {
    try {
      const response = await fetch('/api/account/home_preferences', {
        method: 'PATCH',
        body: preferences
      });
      
      if (response.ok) {
        // Update the displayed preferences immediately
        setHomePreferences(preferences);
        
        // Close dialog and show success message
        setPreferencesDialogOpen(false);
        setPreferencesMessage({ 
          type: 'success', 
          text: 'Home preferences updated successfully' 
        });
        setSnackbarOpen(true);
      } else {
        const data = await response.json();
        setPreferencesMessage({ 
          type: 'error', 
          text: data.error || 'Failed to update preferences' 
        });
        setErrorMessage(data.error || 'Failed to update preferences');
        setErrorSnackbarOpen(true);
      }
      return Promise.resolve();
    } catch (error) {
      const errorText = error instanceof Error ? error.message : 'An error occurred';
      setPreferencesMessage({ type: 'error', text: errorText });
      setErrorMessage(errorText);
      setErrorSnackbarOpen(true);
      return Promise.resolve();
    }
  };

  const handleSubmit = async (homeName: string, address: string, details: {
    bedrooms: number;
    bathrooms: number;
    sqft: number;
    price: number;
  }) => {
    const result = await createHome(homeName, address, details);
    if (result.success) {
      handleDialogClose();
      setSnackbarOpen(true);
    } else {
      setErrorMessage(result.error || 'Failed to create home');
      setErrorSnackbarOpen(true);
    }
  };

  const handleDelete = async (id: string) => {
    const result = await deleteHome(id);
    if (!result.success) {
      setErrorMessage(result.error || 'Failed to delete home');
      setErrorSnackbarOpen(true);
    }
  };

  const handleEdit = (id: string) => {
    const home = homes.find(home => home.id === id);
    if (home) {
      // Parse details to get address if it's in JSON format
      let address = '';
      let bedrooms = 3;
      let bathrooms = 2;
      let sqft = 1800;
      let price = 350000;
      
      try {
        if (typeof home.details === 'string' && home.details.startsWith('{')) {
          const detailsObj = JSON.parse(home.details);
          address = detailsObj.address || '';
          bedrooms = detailsObj.bedrooms || 3;
          bathrooms = detailsObj.bathrooms || 2;
          sqft = detailsObj.sqft || 1800;
          price = detailsObj.price || 350000;
        } else {
          address = home.details || '';
        }
      } catch (e) {
        console.error('Failed to parse home details:', e);
        address = home.details || '';
      }
      
      setHomeToEdit({
        name: home.name,
        address: address,
        bedrooms: bedrooms,
        bathrooms: bathrooms,
        sqft: sqft,
        price: price
      });
      setEditHomeId(id);
      setEditModalOpen(true);
    }
  };

  const handleEditClose = () => {
    setEditModalOpen(false);
    setEditHomeId(null);
  };

  const handleEditSubmit = async (homeName: string, address: string, details: {
    bedrooms: number;
    bathrooms: number;
    sqft: number;
    price: number;
  }) => {
    if (!editHomeId) return;
    
    // Pass all params to the updateHome function
    const result = await updateHome(editHomeId, homeName, address, details);
    
    if (result.success) {
      handleEditClose();
      setSnackbarOpen(true);
    } else {
      setErrorMessage(result.error || 'Failed to update home');
      setErrorSnackbarOpen(true);
    }
  };

  useEffect(() => {
    fetchHomes();
  }, []);

  useEffect(() => {
    if (dialogOpen) {
      // Small delay to ensure dialog transition completes
      const timer = setTimeout(() => {
        textFieldRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [dialogOpen]);

  // Add handler to close snackbar
  const handleSnackbarClose = () => {
    setErrorSnackbarOpen(false);
    setErrorMessage('');
  };
  
  const handleHomeClick = (homeId: string) => {
    navigate(`/home/${homeId}`);
  };

  return (
    <BasePage
      title="My Homes"
      description="View and manage your saved homes."
      keywords="homes, real estate, property management"
      canonicalUrl="/homes"
    >
      {isLoading ? (
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '50vh' 
          }}
        >
          <CircularProgress size={40} />
        </Box>
      ) : (
        <div style={{ margin: '24px' }}>
          <Box sx={{ mb: 4, textAlign: 'left' }}>
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom 
              sx={{ 
                fontWeight: 400, 
                letterSpacing: '0.5px',
                color: 'primary.dark',
                fontFamily: '"Roboto Slab", "Roboto", "Helvetica", "Arial", sans-serif'
              }}
            >
              My Homes
            </Typography>
            <Typography 
              variant="body2"
              sx={{ 
                color: 'text.secondary', 
                fontWeight: 300,
                fontStyle: 'italic',
                maxWidth: '600px'
              }}
            >
              The homes you have walked through can be found here.
            </Typography>
          </Box>
          {homes.length === 0 ? (
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '40vh',
                textAlign: 'center',
                px: 2
              }}
            >
              <Typography 
                variant="h5" 
                component="h2" 
                gutterBottom
                sx={{ fontWeight: 500 }}
              >
                No homes yet
              </Typography>
              <Typography 
                variant="body1" 
                color="text.secondary"
                sx={{ mb: 4, maxWidth: '500px' }}
              >
                You can change that by adding and saving a house to CribCritic.
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                gap: 2,
                justifyContent: 'center',
                flexDirection: { xs: 'column', sm: 'row' },
                width: { xs: '100%', sm: 'auto' }
              }}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="medium"
                  startIcon={<TuneIcon />}
                  onClick={handlePreferencesOpen}
                  sx={{ 
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    minWidth: { xs: '100%', sm: 'auto' }
                  }}
                >
                  Preferences
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  size="medium"
                  startIcon={<AddIcon />}
                  onClick={handleDialogOpen}
                  sx={{ 
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    minWidth: { xs: '100%', sm: 'auto' }
                  }}
                >
                  Add New Home
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <Box sx={{ 
                mb: 3, 
                px: { xs: 2, sm: 4 },
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 2
              }}>
                <SearchField
                  placeholder="Search homes"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ 
                    width: '100%', 
                    flexGrow: 1,
                    maxWidth: { sm: '500px' }
                  }}
                />
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1,
                  alignSelf: { xs: 'flex-end', sm: 'auto' }
                }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="medium"
                    startIcon={<TuneIcon />}
                    onClick={handlePreferencesOpen}
                    sx={{ 
                      textTransform: 'none'
                    }}
                  >
                    Preferences
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="medium"
                    startIcon={<AddIcon />}
                    onClick={handleDialogOpen}
                    sx={{ 
                      textTransform: 'none'
                    }}
                  >
                    New Home
                  </Button>
                </Box>
              </Box>

              <HomesGrid
                homes={homes}
                filteredHomes={filteredHomes}
                deletingHomeIds={deletingHomeIds}
                onDelete={handleDelete}
                onEdit={handleEdit}
                onHomeClick={handleHomeClick}
                searchQuery={searchQuery}
              />
            </>
          )}
        </div>
      )}
      
      <AddHomeForm
        open={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />
      
      <AddHomeForm
        open={editModalOpen}
        onClose={handleEditClose}
        onSubmit={handleEditSubmit}
        isSubmitting={isSubmitting}
        isEdit={true}
        initialValues={homeToEdit}
        title="Edit Home"
        submitButtonText="Update"
      />
      
      <HomePreferencesDialog
        open={preferencesDialogOpen}
        onClose={handlePreferencesClose}
        currentPreferences={homePreferences}
        onSave={handleSavePreferences}
        title="Your Home Preferences"
        description="Describe what you're looking for in a home. This helps CribCritic AI provide more relevant insights and recommendations."
      />
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          {preferencesMessage.text || 'Home created successfully!'}
        </Alert>
      </Snackbar>
      <Snackbar 
        open={errorSnackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </BasePage>
  );
}

export default Homes;